/** Ranges: a model or brand line sold in several flavours or colours. */
const ranges = {
  en: {
    title: "Ranges",
    subtitle:
      "A model or brand line sold in several flavours or colours — a puff bar, a pod kit, a salt juice brand. Each variant stays its own product with its own stock; the register folds them into one tile.",
    new: "New range",
    emptyTitle: "No ranges yet",
    emptyHint:
      "Create one, paste its flavours or colours, and every variant becomes a product in one go.",
    loadFailed: "Could not load ranges",
    saveFailed: "Could not save the range",
    card: {
      variants: { one: "{count} variant", other: "{count} variants" },
      inStock: "{count} in stock",
    },
    form: {
      newTitle: "New range",
      editTitle: "Edit range",
      name: "Range name *",
      namePlaceholder: "e.g. Vozol Gear 12000",
      nameHint: "Each variant is named “<range name> <variant>”, e.g. Vozol Gear 12000 Mint",
      copyNote:
        "These values are copied into each variant when it is created. Changing them later does not change existing variants unless you choose “Apply to variants”.",
      save: "Save range",
      saving: "Saving…",
    },
    detail: {
      edit: "✏️ Edit",
      apply: "Apply to variants",
      delete: "🗑️ Delete",
      buyPrice: "buy {amount}",
      deleteConfirm:
        "Delete this range? Its {count} products are kept and simply become ordinary products.",
      deleteFailed: "Could not delete the range",
      addTitle: "Add variants",
      addPlaceholder: "Watermelon Ice\nMango Ice\nCool Mint",
      addHint:
        "One per line — flavours or colours. Each becomes a product with this range's price, brand and picture, at 0 stock. Names already in the range are skipped.",
      addButton: "Add variants",
      added: "Added {added}, skipped {skipped} already in the range.",
      addFailed: "Could not add the variants",
      variants: "Variants",
      noVariants: "No variants yet — paste a list above.",
      colPicture: "Picture",
      colVariant: "Variant",
      colPrice: "Price",
      colStock: "Stock",
      colShopStock: "In shop",
      colWarehouseStock: "Warehouse",
      stockOf: "Stock of {name}",
      saveStock: "Save stock ({count})",
      stockSaved: "Stock updated for {count} variants.",
      stockNote: "Set from the range screen",
      stockFailed: "Could not update the stock",
      editProduct: "Edit",
      remove: "Remove",
      removeConfirm:
        "Take “{name}” out of this range? It stays in your products.",
      removeFailed: "Could not remove it from the range",
    },
    apply: {
      title: "Apply to variants",
      intro:
        "Overwrites the fields you tick on all {count} variants with this range's values. Names, variant labels and stock are never touched.",
      fields: {
        price: "Sell price",
        cost_price: "Buy price",
        brand: "Brand",
        category: "Category",
        subcategory: "Subcategory",
        image: "Pictures",
        details: "Category details",
      },
      notSet: "not set",
      confirm: "Apply to {count} variants",
      done: "Updated {count} products.",
      failed: "Could not apply to the variants",
    },
  },

  fr: {
    title: "Gammes",
    subtitle:
      "Un modèle ou une marque vendu en plusieurs saveurs ou couleurs — puff, kit pod, e-liquide sels de nicotine. Chaque variante reste un produit avec son propre stock ; la caisse les regroupe en une seule tuile.",
    new: "Nouvelle gamme",
    emptyTitle: "Aucune gamme pour l'instant",
    emptyHint:
      "Créez-en une, collez ses saveurs ou couleurs, et chaque variante devient un produit d'un seul coup.",
    loadFailed: "Impossible de charger les gammes",
    saveFailed: "Impossible d'enregistrer la gamme",
    card: {
      variants: { one: "{count} variante", other: "{count} variantes" },
      inStock: "{count} en stock",
    },
    form: {
      newTitle: "Nouvelle gamme",
      editTitle: "Modifier la gamme",
      name: "Nom de la gamme *",
      namePlaceholder: "ex. Vozol Gear 12000",
      nameHint:
        "Chaque variante est nommée « <nom de la gamme> <variante> », ex. Vozol Gear 12000 Menthe",
      copyNote:
        "Ces valeurs sont copiées dans chaque variante à sa création. Les modifier plus tard ne change pas les variantes existantes, sauf si vous choisissez « Appliquer aux variantes ».",
      save: "Enregistrer la gamme",
      saving: "Enregistrement…",
    },
    detail: {
      edit: "✏️ Modifier",
      apply: "Appliquer aux variantes",
      delete: "🗑️ Supprimer",
      buyPrice: "achat {amount}",
      deleteConfirm:
        "Supprimer cette gamme ? Ses {count} produits sont conservés et deviennent de simples produits.",
      deleteFailed: "Impossible de supprimer la gamme",
      addTitle: "Ajouter des variantes",
      addPlaceholder: "Pastèque glacée\nMangue glacée\nMenthe fraîche",
      addHint:
        "Une par ligne — saveurs ou couleurs. Chacune devient un produit avec le prix, la marque et l'image de la gamme, à 0 en stock. Les noms déjà présents sont ignorés.",
      addButton: "Ajouter les variantes",
      added: "{added} ajoutée(s), {skipped} déjà dans la gamme ignorée(s).",
      addFailed: "Impossible d'ajouter les variantes",
      variants: "Variantes",
      noVariants: "Aucune variante — collez une liste ci-dessus.",
      colPicture: "Image",
      colVariant: "Variante",
      colPrice: "Prix",
      colStock: "Stock",
      colShopStock: "En boutique",
      colWarehouseStock: "Entrepôt",
      stockOf: "Stock de {name}",
      saveStock: "Enregistrer le stock ({count})",
      stockSaved: "Stock mis à jour pour {count} variante(s).",
      stockNote: "Saisi depuis l'écran de la gamme",
      stockFailed: "Impossible de mettre à jour le stock",
      editProduct: "Modifier",
      remove: "Retirer",
      removeConfirm:
        "Retirer « {name} » de cette gamme ? Il reste dans vos produits.",
      removeFailed: "Impossible de le retirer de la gamme",
    },
    apply: {
      title: "Appliquer aux variantes",
      intro:
        "Remplace les champs cochés sur les {count} variantes par les valeurs de cette gamme. Les noms, libellés de variante et stocks ne sont jamais modifiés.",
      fields: {
        price: "Prix de vente",
        cost_price: "Prix d'achat",
        brand: "Marque",
        category: "Catégorie",
        subcategory: "Sous-catégorie",
        image: "Images",
        details: "Détails de la catégorie",
      },
      notSet: "non défini",
      confirm: "Appliquer à {count} variantes",
      done: "{count} produit(s) mis à jour.",
      failed: "Impossible d'appliquer aux variantes",
    },
  },

  ar: {
    title: "المجموعات",
    subtitle:
      "موديل أو علامة تُباع بعدة نكهات أو ألوان — بافّ، جهاز بود، أو سائل أملاح النيكوتين. يبقى كل خيار منتجاً بمخزونه الخاص، وتجمعها شاشة البيع في بطاقة واحدة.",
    new: "مجموعة جديدة",
    emptyTitle: "لا توجد مجموعات بعد",
    emptyHint:
      "أنشئ مجموعة والصق نكهاتها أو ألوانها، فيصبح كل خيار منتجاً دفعة واحدة.",
    loadFailed: "تعذّر تحميل المجموعات",
    saveFailed: "تعذّر حفظ المجموعة",
    card: {
      variants: {
        one: "خيار واحد",
        two: "خياران",
        few: "{count} خيارات",
        many: "{count} خياراً",
        other: "{count} خيار",
      },
      inStock: "{count} في المخزون",
    },
    form: {
      newTitle: "مجموعة جديدة",
      editTitle: "تعديل المجموعة",
      name: "اسم المجموعة *",
      namePlaceholder: "مثال: Vozol Gear 12000",
      nameHint: "يُسمّى كل خيار «<اسم المجموعة> <الخيار>» مثل Vozol Gear 12000 نعناع",
      copyNote:
        "تُنسخ هذه القيم إلى كل خيار عند إنشائه. تغييرها لاحقاً لا يغيّر الخيارات الموجودة إلا إذا اخترت «تطبيق على الخيارات».",
      save: "حفظ المجموعة",
      saving: "جارٍ الحفظ…",
    },
    detail: {
      edit: "✏️ تعديل",
      apply: "تطبيق على الخيارات",
      delete: "🗑️ حذف",
      buyPrice: "الشراء {amount}",
      deleteConfirm:
        "حذف هذه المجموعة؟ تبقى منتجاتها ({count}) وتصبح منتجات عادية.",
      deleteFailed: "تعذّر حذف المجموعة",
      addTitle: "إضافة خيارات",
      addPlaceholder: "بطيخ مثلج\nمانجو مثلج\nنعناع بارد",
      addHint:
        "خيار في كل سطر — نكهات أو ألوان. يصبح كل واحد منتجاً بسعر المجموعة وعلامتها وصورتها، بمخزون 0. تُتجاهل الأسماء الموجودة مسبقاً.",
      addButton: "إضافة الخيارات",
      added: "أُضيف {added}، وتُجوهل {skipped} موجود مسبقاً في المجموعة.",
      addFailed: "تعذّرت إضافة الخيارات",
      variants: "الخيارات",
      noVariants: "لا توجد خيارات بعد — الصق قائمة في الأعلى.",
      colPicture: "الصورة",
      colVariant: "الخيار",
      colPrice: "السعر",
      colStock: "المخزون",
      colShopStock: "في المتجر",
      colWarehouseStock: "المخزن",
      stockOf: "مخزون {name}",
      saveStock: "حفظ المخزون ({count})",
      stockSaved: "تم تحديث مخزون {count} خيار.",
      stockNote: "أُدخل من شاشة المجموعة",
      stockFailed: "تعذّر تحديث المخزون",
      editProduct: "تعديل",
      remove: "إزالة",
      removeConfirm: "إزالة «{name}» من هذه المجموعة؟ يبقى ضمن منتجاتك.",
      removeFailed: "تعذّرت إزالته من المجموعة",
    },
    apply: {
      title: "تطبيق على الخيارات",
      intro:
        "يستبدل الحقول التي تحددها في جميع الخيارات ({count}) بقيم هذه المجموعة. لا تتغير الأسماء ولا تسميات الخيارات ولا المخزون أبداً.",
      fields: {
        price: "سعر البيع",
        cost_price: "سعر الشراء",
        brand: "العلامة",
        category: "الفئة",
        subcategory: "الفئة الفرعية",
        image: "الصور",
        details: "تفاصيل الفئة",
      },
      notSet: "غير محدد",
      confirm: "تطبيق على {count} خيار",
      done: "تم تحديث {count} منتج.",
      failed: "تعذّر التطبيق على الخيارات",
    },
  },
};

export default ranges;
